<template>
	<div class="vcx-document-link">
		<BaseLink
			class="vcx-document-link__link vcx-flex vcx-gap-100 vcx-items-center vcx-p-100"
			:link="{ href: url, target: '_blank' }"
		>
			<div class="vcx-document-link__icon-container vcx-flex vcx-items-center vcx-justify-center">
				<div class="vcx-document-link__icon vcx-flex vcx-items-center vcx-justify-center">
					<BaseSvgIcon width="20" height="20" :name="icon" />
				</div>
			</div>
			<div class="vcx-document-link__content vcx-flex vcx-flex-col">
				<div class="vcx-document-link__title vcx-body-lg vcx-text-inverse">{{ props.literature.title }}</div>
				<div class="vcx-document-link__subtitle vcx-body-md vcx-text-tertiary" :data-test="type">{{
					type
				}}</div>
			</div>
		</BaseLink>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { FundLiterature } from 'types/funds';

interface Props {
	literature: FundLiterature;
}

const props = defineProps<Props>();

const isEdgarLink = computed((): boolean => props.literature.externalLink?.secFiling ?? false);

const isSanityRedirect = computed(
	(): boolean =>
		!!props.literature.externalLink?.url &&
		(props.literature.externalLink.url.includes('/sanity-documents/') ||
			props.literature.externalLink.url.includes('/documents/'))
);

// As per Sanity schema either file or external link has be added in order to publish literature item
const url = computed((): string => props.literature.file?.url ?? props.literature.externalLink?.url ?? '');

const type = computed((): string => {
	if (!!props.literature.file?.url && !!props.literature.file?.extension) {
		return `${props.literature.file.extension.toUpperCase()} attachment`;
	} else if (isSanityRedirect.value) {
		return 'PDF attachment';
	} else if (isEdgarLink.value) {
		return 'EDGAR SEC link';
	}

	return 'External link';
});

const icon = computed((): string => (props.literature.file?.url || isSanityRedirect.value ? 'pdf' : 'external-link'));
</script>

<style lang="scss">
.vcx-document-link {
	&__link {
		height: 100%;
		border: 1px solid var(--vcx-gray-50);
		border-radius: var(--vcx-radius-50);
		text-decoration: none;
		transition: all var(--vcx-transition-fast);

		&:hover {
			background-color: var(--vcx-border-rust);
			border-color: var(--vcx-brand-primary);
		}
	}

	&__icon-container {
		flex-shrink: 0;
		width: 34px;
		height: 44px;
		background-color: var(--vcx-gray-white);
		box-shadow: var(--vcx-shadow-25);
	}

	&__icon {
		color: var(--vcx-content-primary);
	}

	&__content {
		gap: 0;
		flex: 1 0 0;
		min-width: 0;
	}

	&__title {
		word-wrap: break-word;
		overflow-wrap: break-word;
	}

	&__subtitle {
		margin-top: 0;
	}
}
</style>
